/**
 * Автоконтент (§3.3–3.4): посты, которые Claude готовит сам.
 * Шапка с «Подобрать свежие сейчас» и «Нужен пост», строка «Следующая подборка», живые панели задач
 * (только пока Claude работает), вкладки «Ждут решения / Запланированы / Опубликованы» и сетка карточек 3 в ряд.
 */
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { CalendarClock, CalendarOff, Hourglass, RefreshCw, Sparkles, TriangleAlert } from "lucide-react";
import type { AutoStatus, JobSummary, Post, PostStatus } from "@impuls/shared";
import { api, ApiError, errText } from "../lib/api";
import { countText, humanDateTime, plural, sameDay, time } from "../lib/format";
import { useSettings } from "../lib/queries";
import { AutoCard } from "../components/AutoCard";
import { AutoJobPanel, useAutoJobs } from "../components/AutoJobs";
import { AutoNeedPost } from "../components/AutoNeedPost";
import { EcgLoader } from "../components/Ecg";
import { Tabs } from "../components/PostBits";
import { EmptyState, LoadError, PageHeader, useToast } from "../components/ui";

type Tab = Extract<PostStatus, "draft" | "scheduled" | "published">;
const TAB_ORDER: Tab[] = ["draft", "scheduled", "published"];
const TAB_LABEL: Record<Tab, string> = { draft: "Ждут решения", scheduled: "Запланированы", published: "Опубликованы" };
// «Ждут решения» — и посты, которые не вышли во ВК: их видно с ошибкой
const TAB_STATUSES: Record<Tab, string> = { draft: "draft,failed", scheduled: "scheduled", published: "published" };

const useAutoPosts = (tab: Tab) =>
  useQuery({ queryKey: ["posts", "auto", tab], queryFn: () => api.get<Post[]>(`/api/posts?group=auto&status=${TAB_STATUSES[tab]}`) });

export function AutoPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: Tab = TAB_ORDER.includes(tabParam as Tab) ? (tabParam as Tab) : "draft";

  const status = useQuery({
    queryKey: ["auto-status"],
    queryFn: () => api.get<AutoStatus>("/api/auto/status"),
    refetchInterval: 30_000,
  });
  const jobs = useAutoJobs(status.data?.running);
  const lists: Record<Tab, ReturnType<typeof useAutoPosts>> = {
    draft: useAutoPosts("draft"),
    scheduled: useAutoPosts("scheduled"),
    published: useAutoPosts("published"),
  };
  const [needOpen, setNeedOpen] = useState(false);
  const [runBusy, setRunBusy] = useState(false);

  const setTab = (t: Tab) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (t === "draft") next.delete("tab");
        else next.set("tab", t);
        return next;
      },
      { replace: true },
    );

  const runNow = async () => {
    setRunBusy(true);
    try {
      const r = await api.post<{ jobId: number }>("/api/auto/run-now");
      jobs.add(r.jobId);
      void qc.invalidateQueries({ queryKey: ["auto-status"] });
      toast("Подборка запущена", "info");
    } catch (e) {
      toast(errText(e), e instanceof ApiError && e.status === 409 ? "info" : "error");
    } finally {
      setRunBusy(false);
    }
  };

  const started = (jobId: number, count: number) => {
    jobs.add(jobId);
    void qc.invalidateQueries({ queryKey: ["auto-status"] });
    toast(`Claude готовит ${count === 1 ? "пост" : countText(count, "пост", "поста", "постов")}`, "info");
  };

  const running = status.data?.running ?? [];
  const current = lists[tab];

  return (
    <div className="page auto">
      <PageHeader
        title="Автоконтент"
        actions={
          <>
            <button className="btn auto-head-btn" onClick={() => void runNow()} disabled={runBusy}>
              <RefreshCw size={17} aria-hidden="true" className={runBusy ? "auto-spin" : ""} />
              Подобрать свежие сейчас
            </button>
            <button className="btn btn--primary auto-head-btn" onClick={() => setNeedOpen(true)}>
              <Sparkles size={17} aria-hidden="true" />
              Нужен пост
            </button>
          </>
        }
      />

      {status.data ? (
        <StatusLine s={status.data} onRetry={() => void runNow()} retryBusy={runBusy} />
      ) : status.error ? (
        <div className="error-line auto-status-error" role="alert">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>
            Не получилось узнать расписание подборки: {errText(status.error)}{" "}
            <button className="auto-linkbtn" onClick={() => void status.refetch()}>
              Повторить
            </button>
          </span>
        </div>
      ) : (
        <div className="auto-status auto-status--loading">
          <EcgLoader label="Узнаю расписание подборки" />
        </div>
      )}

      {jobs.ids.length > 0 && (
        <section className="auto-jobs" aria-labelledby="auto-jobs-title">
          <h2 className="auto-section-title" id="auto-jobs-title">
            Что делает Claude
          </h2>
          {jobs.ids.map((id) => (
            <AutoJobPanel
              key={id}
              id={id}
              defaultOpen={jobs.live.has(id)}
              onDismiss={() => jobs.dismiss(id)}
              onFinished={(at) => jobs.finish(id, at)}
              onRetried={(newId) => {
                jobs.dismiss(id);
                jobs.add(newId);
              }}
            />
          ))}
        </section>
      )}

      <div className="auto-bar">
        <div className="auto-tabs">
          <Tabs<Tab>
            label="Автопосты по статусу"
            value={tab}
            onChange={setTab}
            tabs={TAB_ORDER.map((t) => ({ value: t, label: TAB_LABEL[t], count: lists[t].data?.length }))}
          />
        </div>
      </div>

      <div role="tabpanel" aria-label={TAB_LABEL[tab]} className="auto-panel">
        {current.isLoading ? (
          <div className="auto-loading">
            <EcgLoader label="Загружаю посты" />
          </div>
        ) : current.error ? (
          <LoadError error={current.error} retry={() => void current.refetch()} />
        ) : current.data && current.data.length > 0 ? (
          <div className="auto-grid">
            {current.data.map((p) => (
              <AutoCard key={p.id} post={p} />
            ))}
          </div>
        ) : (
          <Empty
            tab={tab}
            busy={running.length > 0}
            drafts={lists.draft.data?.length ?? 0}
            onNeed={() => setNeedOpen(true)}
            onTab={setTab}
          />
        )}
      </div>

      <AutoNeedPost open={needOpen} onClose={() => setNeedOpen(false)} onStarted={started} />
    </div>
  );
}

/* ================= Строка статуса ================= */
function everyText(days: number, at: string) {
  return days <= 1 ? `каждый день в ${at}` : `раз в ${days} ${plural(days, "день", "дня", "дней")} в ${at}`;
}

/** Почему повтор: лимит подписки — так и пишем, иначе — ошибка прошлой попытки. */
function retryReason(lj: JobSummary | null): string {
  if (!lj) return "";
  if (/лимит/i.test(lj.info)) return "закончился лимит подписки Claude";
  return lj.status === "failed" ? lj.info : "";
}

/** Одна строка: когда следующая подборка. Итог прошлой не показываем — только если она упала. */
function StatusLine({ s, onRetry, retryBusy }: { s: AutoStatus; onRetry: () => void; retryBusy: boolean }) {
  const { data: settings } = useSettings();
  const now = Date.now();
  const retrying = !!s.retry_at && s.retry_at > now;

  let icon: ReactNode;
  let tone = "";
  let title: string;
  let text: ReactNode;
  if (!s.enabled) {
    icon = <CalendarOff size={17} />;
    title = "Плановая подборка выключена";
    text = <Link to="/settings#auto">Включить в Настройках</Link>;
  } else if (retrying) {
    icon = <Hourglass size={17} />;
    tone = "warn";
    const at = s.retry_at!;
    title = `Повтор подборки — ${sameDay(at, now) ? time(at) : humanDateTime(at)}`;
    text = retryReason(s.last_job);
  } else {
    const at = s.next_run ? time(s.next_run) : `${s.hour}:00`;
    const parts = settings
      ? [
          settings.auto_news_count > 0 && countText(settings.auto_news_count, "новость", "новости", "новостей"),
          settings.auto_fact_count > 0 && countText(settings.auto_fact_count, "факт", "факта", "фактов"),
          settings.auto_myth_count > 0 && countText(settings.auto_myth_count, "миф", "мифа", "мифов"),
        ].filter(Boolean)
      : [];
    const what = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} и ${parts.at(-1)}` : parts.join("");
    const overdue = s.next_run !== null && s.next_run <= now;
    icon = <CalendarClock size={17} />;
    title = overdue ? "Следующая подборка — уже пора" : `Следующая подборка — ${humanDateTime(s.next_run)}`;
    text = overdue ? "начнётся в ближайшие минуты" : `${everyText(s.interval_days, at)}${what ? `: ${what}` : ""}`;
  }

  // упавшая плановая подборка — ошибка, её видно, пока не пройдёт следующая
  const lj = s.last_job;
  const failed = lj?.status === "failed" && !retrying && s.running.length === 0 ? lj : null;

  return (
    <>
      <section className="auto-status" aria-label="Статус подборки">
        <span className={`auto-status__icon ${tone ? `auto-status__icon--${tone}` : ""}`} aria-hidden="true">
          {icon}
        </span>
        <p className="auto-status__line">
          <span className="auto-status__title">{title}</span>
          {text && <span className="auto-status__text">{text}</span>}
        </p>
      </section>
      {failed && (
        <div className="error-line auto-status-fail" role="alert">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>
            Подборка {humanDateTime(failed.started_at)} не удалась: {failed.info}{" "}
            <button className="auto-linkbtn" onClick={onRetry} disabled={retryBusy}>
              Повторить
            </button>
          </span>
        </div>
      )}
    </>
  );
}

/* ================= Пустые вкладки ================= */
function Empty({
  tab,
  busy,
  drafts,
  onNeed,
  onTab,
}: {
  tab: Tab;
  busy: boolean;
  drafts: number;
  onNeed: () => void;
  onTab: (t: Tab) => void;
}) {
  if (tab === "draft") {
    return busy ? (
      <EmptyState mascot="hug" title="Claude готовит посты" text="Появятся здесь, как только он закончит." />
    ) : (
      <EmptyState
        title="Новых постов нет"
        action={
          <button className="btn btn--primary" onClick={onNeed}>
            <Sparkles size={17} aria-hidden="true" />
            Нужен пост
          </button>
        }
      />
    );
  }
  if (tab === "scheduled") {
    return (
      <EmptyState
        mascot="hug"
        title="В расписании пусто"
        action={
          drafts > 0 ? (
            <button className="btn btn--dark" onClick={() => onTab("draft")}>
              К постам, которые ждут решения
            </button>
          ) : (
            <button className="btn btn--primary" onClick={onNeed}>
              <Sparkles size={17} aria-hidden="true" />
              Нужен пост
            </button>
          )
        }
      />
    );
  }
  return (
    <EmptyState
      mascot="hug"
      title="Опубликованных автопостов пока нет"
      action={
        <Link className="btn btn--dark" to="/calendar">
          Открыть календарь
        </Link>
      }
    />
  );
}
